import { RiskLevelEnum, type RegionalOverviewV2Properties } from 'querys/regional-overview/regional-overview.query.models';
import {
  ALL_FILTER_VALUE,
  RegionalOverviewFilterType,
  buildFilter,
  type Filter
} from '@features/regional-overview/regional-overview.models';
import { computeCalculatedRisk, isAllOrEmpty } from './builders.utils';
import i18n from 'i18next';
import { sortFilterOptionsByLabel } from '../filter.utils';

export const buildDiseaseFilter = <P extends RegionalOverviewV2Properties>(): Filter =>
  buildFilter<P>({
    type: RegionalOverviewFilterType.DISEASE,
    defaultValue: [ALL_FILTER_VALUE],
    includeAllOption: true,
    translatable: true,
    sortOptions: options => sortFilterOptionsByLabel(options, option => String(i18n.t(option.labelKey)), i18n.language),
    getValues: feature => (feature.properties?.values?.risks?.diseases ?? []).map(disease => disease.disease),
    matches: (feature, value) => {
      if (isAllOrEmpty(value)) return true;
      const selection = value as string[];
      return (feature.properties?.values?.risks?.diseases ?? []).some(disease => selection.includes(disease.disease));
    },
    prepare: (feature, value) => {
      const risks = feature.properties?.values?.risks;
      if (!risks) return feature;
      const nextProperties = feature.properties ? { ...feature.properties } : undefined;
      if (!nextProperties || !nextProperties.values) return feature;

      let nextDiseases = risks.diseases ?? [];
      if (!isAllOrEmpty(value)) {
        const selection = new Set(value as string[]);
        nextDiseases = nextDiseases.filter(disease => selection.has(disease.disease));
      }

      const calculatedRisk = computeCalculatedRisk(nextDiseases, Boolean(nextProperties.values.error));

      nextProperties.calculatedRisk = calculatedRisk;
      nextProperties.values = {
        ...nextProperties.values,
        risks: { ...risks, diseases: nextDiseases, risk: calculatedRisk as RiskLevelEnum }
      };
      return { ...feature, properties: nextProperties };
    }
  });
